/**
 * [NAS 저장] 단추 — 문서 목록·상세 어디에나 붙이는 공용 부품 (v2.95.0 · 2026-08-26).
 *
 * 사용자 지시 (2026-08-26)
 *   · 자동 보관이 아니라 **누를 때 저장**한다. 여러 번 눌러 여러 개가 쌓여도 괜찮다.
 *   · **영업건 연동이 없으면 단추를 비활성**으로 두고, 마우스를 올리면 그 이유를 보여 준다.
 *   · 그 상태에서 **바로 연동할 수 있게** 이어 준다 → `onRequestLink` 를 주면 눌러서 연동 창이 뜬다.
 *     (연동 창을 줄 수 없는 화면에서는 진짜로 눌리지 않는 비활성 단추가 된다.)
 *
 * ⚠ 저장 여부 판단(`canSave`)은 **화면이 이미 아는 값**으로 넘긴다 — 목록 한 줄마다 서버에
 *   물어보면 스무 줄이면 스무 번이다. 서버는 저장할 때 어차피 다시 확인한다(그쪽이 진짜 판정).
 */

import { useState } from 'react';
import { saveToNas, type NasDocType } from '../lib/nas-api';

interface Props {
  docType: NasDocType;
  docId: number;
  /** 저장할 수 있는 상태인가 (예: 영업건이 연동돼 있는가) */
  canSave: boolean;
  /** 저장할 수 없을 때 보여 줄 이유 */
  blockedReason?: string;
  /** 주면, 비활성 상태에서 눌렀을 때 연동 창을 연다 */
  onRequestLink?: () => void;
  /** 저장 성공 후 (목록 새로고침 등) */
  onSaved?: () => void;
  /** 표 안에서는 'icon', 상세 화면에서는 'full' */
  variant?: 'icon' | 'full';
}

export function NasSaveButton({
  docType,
  docId,
  canSave,
  blockedReason = '연동된 영업건이 없어 저장할 폴더를 정할 수 없습니다.',
  onRequestLink,
  onSaved,
  variant = 'icon',
}: Props) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const handleSave = async () => {
    setBusy(true);
    setDone(null);
    try {
      const r = await saveToNas(docType, docId);
      setDone(r.path);
      // 저장 위치를 그대로 보여 준다 — 어디 갔는지 모르면 확인할 수가 없다.
      alert(
        `NAS 에 저장했습니다.\n\n${r.path}` +
          (r.renamed ? '\n\n※ 같은 이름이 이미 있어 이름을 비켜 저장했습니다.' : ''),
      );
      onSaved?.();
    } catch (e) {
      alert('NAS 저장 실패\n\n' + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // ── 저장할 수 없는 상태 ──────────────────────────────
  if (!canSave) {
    const canLinkHere = typeof onRequestLink === 'function';
    const title = canLinkHere
      ? `${blockedReason}\n눌러서 지금 영업건을 연동할 수 있습니다.`
      : blockedReason;
    return (
      <button
        type="button"
        // ⚠ 진짜 disabled 로 두면 마우스를 올려도 설명이 안 뜨는 브라우저가 있다.
        //   연동 창을 줄 수 없을 때만 실제 비활성으로 만든다.
        disabled={!canLinkHere}
        onClick={canLinkHere ? onRequestLink : undefined}
        title={title}
        className={`btn btn-secondary text-slate-400 ${variant === 'icon' ? 'btn-sm' : ''}`}
      >
        {variant === 'icon' ? '💾' : '💾 NAS 저장'}
      </button>
    );
  }

  // ── 저장 가능 ────────────────────────────────────────
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => void handleSave()}
      title={done ? `저장됨: ${done}` : 'NAS 에 저장합니다'}
      className={`btn btn-success ${variant === 'icon' ? 'btn-sm' : ''}`}
    >
      {busy ? '저장 중…' : variant === 'icon' ? '💾' : '💾 NAS 저장'}
    </button>
  );
}
